// src/features/hotels/components/HotelEdit.tsx

import React, { useState, useEffect } from 'react';
import {
  Edit,
  SimpleForm,
  TextInput,
  SelectInput,
  NumberInput,
  required,
  useTranslate,
  useRecordContext,
  LoadingIndicator,
  ReferenceInput,
  useDataProvider,
  useNotify,
  FormDataConsumer,
  useRefresh,
} from 'react-admin';
import { useFormContext } from 'react-hook-form';
import {
  Box,
  Grid,
  Alert,
  Typography,
  Chip,
} from '@mui/material';
import {
  Business as HotelIcon,
  Settings as SettingsIcon,
  LocalOffer as TagIcon,
  AccessTime as TimeIcon,
  Check as CheckIcon,
} from '@mui/icons-material';

import { MyToolbar } from '../../../components/forms/MyToolbar';
import { MyFormContainer } from '../../../components/forms/MyFormContainer';
import { 
  MyFormSection, 
  TwoColumnLayout
} from '../../../components/forms/MyFormLayout';
import { useHotelValidation } from '../hooks/useHotelValidation';
import { Hotel, HOTEL_LANGUAGES } from '../types';

const OPERATION_MODE_CHOICES = [
  { id: 'TAG_ONLY', name: 'Tag Only - Solo acepta tags manuales' },
  { id: 'EMAIL_BASED', name: 'Email Based - Permite solicitudes por email' },
];


const HotelEditTitle: React.FC = () => {
  const record = useRecordContext<Hotel>();
  const translate = useTranslate();
  
  if (!record) return <LoadingIndicator />;
  
  return (
    <span>
      {translate('resources.hotels.titles.edit', 'Editar Hotel')}: {record.name}
    </span>
  );
};


const TagTypesSelector: React.FC<{ availableTagTypes: any[]; loading: boolean }> = ({ 
  availableTagTypes, 
  loading 
}) => {
  const translate = useTranslate();
  const { watch, setValue } = useFormContext();
  
  const currentTagTypes = watch('supportedTagTypeIds') || [];
  
  const handleTagTypeToggle = (tagTypeId: string) => {
    const newTagTypes = currentTagTypes.includes(tagTypeId)
      ? currentTagTypes.filter((id: string) => id !== tagTypeId)
      : [...currentTagTypes, tagTypeId];
    
    setValue('supportedTagTypeIds', newTagTypes, { shouldDirty: true });
  };
  
  if (loading) {
    return (
      <Box sx={{ p: 2, textAlign: 'center' }}>
        <LoadingIndicator />
      </Box>
    );
  }
  
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <Typography variant="body2" color="text.secondary">
        {translate('resources.hotels.help.select_tag_types', 
          'Seleccione los tipos de acceso que el hotel aceptará')}
      </Typography>
      
      {availableTagTypes.length > 0 ? (
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5 }}>
          {availableTagTypes.map((tagType) => {
            const isSelected = currentTagTypes.includes(tagType.id);
            return (
              <Chip
                key={tagType.id}
                icon={isSelected ? <CheckIcon /> : undefined}
                label={tagType.name || tagType.displayName}
                onClick={() => handleTagTypeToggle(tagType.id)}
                color={isSelected ? 'primary' : 'default'}
                variant={isSelected ? 'filled' : 'outlined'}
                sx={{ 
                  cursor: 'pointer',
                  transition: 'all 0.2s',
                  '&:hover': {
                    transform: 'scale(1.05)',
                  }
                }}
              />
            );
          })}
        </Box>
      ) : (
        <Alert severity="warning">
          {translate('resources.hotels.no_tag_types', 
            'No hay tipos de tag disponibles. Contacte al administrador del sistema.')}
        </Alert>
      )}
      
      <FormDataConsumer>
        {({ formData }) => (
          currentTagTypes.length === 0 && (
            <Alert severity="error">
              {translate('resources.hotels.alerts.tag_required', 
                'Debe seleccionar al menos un tipo de ID')}
            </Alert>
          )
        )}
      </FormDataConsumer>
    </Box>
  );
};


const HotelEditForm: React.FC = () => {
  const translate = useTranslate();
  const record = useRecordContext<Hotel>();
  const dataProvider = useDataProvider() as any;
  const notify = useNotify();
  const { reset } = useFormContext();
  
  const [availableTagTypes, setAvailableTagTypes] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  
  const { 
    validateHotelName, 
    validateAddress, 
    validateInstallationCode,
    validateDefaultLanguage,
  } = useHotelValidation();

  useEffect(() => {
    if (!record?.id) return;

    const loadGeneralConfiguration = async () => {
      setLoading(true);
      try {
        // Cargar tipos de tag disponibles
        const tagTypesResponse = await dataProvider.getList('tag-types', {
          pagination: { page: 1, perPage: 100 },
          sort: { field: 'name', order: 'ASC' },
          filter: {},
        });

        // Cargar configuración general del hotel
        const configResponse = await dataProvider.getGeneralConfiguration(record.id);
        
        const availableTags = tagTypesResponse.data || [];
        const config = configResponse.data || {};
        
        setAvailableTagTypes(availableTags);
        
        // Preparar los datos del formulario
        const formData = {
          ...record,
          ...config,
          groupId: config.groupId || record.group?.id,
          operationMode: config.operationMode || 'TAG_ONLY',
          defaultLanguage: config.defaultLanguage || 'EN',
          emailAssignmentDuration: config.emailAssignmentDuration || 24,
          codeExpirationMinutes: config.codeExpirationMinutes || 60,
          lockerHoldMinutes: config.lockerHoldMinutes || 30,
          supportedTagTypeIds: config.supportedTagTypes?.map((t: any) => t.id) || [],
        };
        
        reset(formData);
        
      } catch (error) {
        console.error('Error loading general configuration:', error);
        notify('Error al cargar la configuración del hotel', { type: 'error' });
      } finally {
        setLoading(false);
      }
    };

    loadGeneralConfiguration();
  }, [record, dataProvider, notify, reset]);

  // Validaciones
  const validateHotelNameWithId = (value?: string) => validateHotelName(value, record?.id);
  const validateInstallationCodeWithId = (value?: string) => validateInstallationCode(value, record?.id);
  
  const validateEmailDuration = (value?: number) => {
    if (value && (value < 1 || value > 168)) {
      return translate('resources.hotels.validation.email_duration', 'Debe estar entre 1 y 168 horas');
    }
    return undefined;
  };
  
  const validateCodeExpiration = (value?: number) => {
    if (value && (value < 5 || value > 1440)) {
      return translate('resources.hotels.validation.code_expiration', 'Debe estar entre 5 y 1440 minutos');
    }
    return undefined;
  };
  
  const validateLockerHold = (value?: number) => {
    if (value && (value < 5 || value > 120)) {
      return translate('resources.hotels.validation.locker_hold', 'Debe estar entre 5 y 120 minutos');
    }
    return undefined;
  };

  const userRole = localStorage.getItem('role');
  const showGroupSelector = userRole === 'SuperAdmin';

 

  return (
    <TwoColumnLayout>
      <MyFormSection
        variant="outlined"
        title={translate('resources.hotels.sections.basic_info', 'Información Básica')}
        subtitle={translate('resources.hotels.sections.basic_info_subtitle', 'Datos principales del hotel')}
        icon={<HotelIcon />}
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
          <TextInput
            source="name"
            label={translate('resources.hotels.fields.name', 'Nombre del Hotel')}
            validate={[required(), validateHotelNameWithId]}
            fullWidth
            helperText={translate('resources.hotels.help.name', 'Nombre único que identifica al hotel')}
          />
          
          {showGroupSelector && (
            <ReferenceInput
              source="groupId"
              reference="groups"
              label={translate('resources.hotels.fields.group', 'Grupo')}
            >
              <SelectInput
                optionText="name"
                optionValue="id"
                fullWidth
                helperText={translate('resources.hotels.help.group', 'Grupo al que pertenece el hotel')}
              />
            </ReferenceInput>
          )}
          
          <TextInput
            source="address"
            label={translate('resources.hotels.fields.address', 'Dirección')}
            validate={validateAddress}
            fullWidth
            multiline
            rows={2}
            helperText={translate('resources.hotels.help.address', 'Dirección física del hotel')}
          />
          
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <TextInput
                source="installationCode"
                label={translate('resources.hotels.fields.installationCode', 'Código de Instalación')}
                validate={validateInstallationCodeWithId}
                fullWidth
                helperText={translate('resources.hotels.help.installationCode', 'Formato: TAyy1234')}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <SelectInput
                source="defaultLanguage"
                label={translate('resources.hotels.fields.defaultLanguage', 'Idioma por Defecto')}
                choices={[...HOTEL_LANGUAGES]}
                validate={[required(), validateDefaultLanguage]}
                fullWidth
                helperText={translate('resources.hotels.help.defaultLanguage', 'Idioma por defecto')}
              />
            </Grid>
          </Grid>
        </Box>
      </MyFormSection>
  
      <MyFormSection
        variant="outlined"
        title={translate('resources.hotels.sections.operation_mode', 'Modo de Operación')}
        subtitle={translate('resources.hotels.sections.operation_mode_subtitle', 'Configuración del sistema de acceso')}
        icon={<SettingsIcon />}
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
          <SelectInput
            source="operationMode"
            label={translate('resources.hotels.fields.operationMode', 'Modo de Operación')}
            choices={OPERATION_MODE_CHOICES}
            validate={required()}
            fullWidth
            helperText={translate('resources.hotels.help.operationMode', 
              'Define cómo los huéspedes acceden a las taquillas')}
          />
          
          <FormDataConsumer>
            {({ formData }) => (
              <>
                {formData.operationMode === 'EMAIL_BASED' && (
                  <>
                    
                    <NumberInput
                      source="emailAssignmentDuration"
                      label={translate('resources.hotels.fields.emailAssignmentDuration', 
                        'Duración de Asignación por Email (horas)')}
                      validate={[required(), validateEmailDuration]}
                      min={1}
                      max={168}
                      step={1}
                      fullWidth
                      helperText={translate('resources.hotels.help.emailAssignmentDuration', 
                        'Tiempo que permanece activa una asignación por email (1-168 horas)')}
                    />
                    
                    <NumberInput
                      source="codeExpirationMinutes"
                      label={translate('resources.hotels.fields.codeExpiration', 
                        'Expiración del Código (minutos)')}
                      validate={[required(), validateCodeExpiration]}
                      min={5}
                      max={1440}
                      step={5}
                      fullWidth
                      helperText={translate('resources.hotels.help.codeExpiration', 
                        'Minutos para usar el código después de generado (5-1440 minutos)')}
                    />
                  </>
                )}
              </>
            )}
          </FormDataConsumer>
          
          <Box sx={{ mt: 2 }}>
            <Typography variant="subtitle2" sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <TimeIcon sx={{ mr: 1, fontSize: 20 }} />
              {translate('resources.hotels.sections.timing_config', 'Configuración de Tiempos')}
            </Typography>
            
            <NumberInput
              source="lockerHoldMinutes"
              label={translate('resources.hotels.fields.lockerHold', 
                'Tiempo de Reserva de Taquilla (minutos)')}
              validate={[required(), validateLockerHold]}
              min={5}
              max={120}
              step={5}
              fullWidth
              helperText={translate('resources.hotels.help.lockerHold', 
                'Minutos que permanece reservada una taquilla sin confirmar (5-120 minutos)')}
            />
          </Box>
        </Box>
      </MyFormSection>

      <MyFormSection
        variant="outlined"
        title={translate('resources.hotels.sections.tag_types', 'Tipos de Tag Soportados')}
        subtitle={translate('resources.hotels.sections.tag_types_subtitle', 
          'Seleccione los tipos de acceso permitidos')}
        icon={<TagIcon />}
      >
        <TagTypesSelector 
          availableTagTypes={availableTagTypes} 
          loading={loading}
        />
      </MyFormSection>
    </TwoColumnLayout>
  );
};


const transformData = async (data: any, { dataProvider }: any) => {
  const hotelId = data.id;
  
  try {
    await dataProvider.updateGeneralConfiguration(hotelId, {
      name: data.name,
      address: data.address,
      installationCode: data.installationCode,
      groupId: data.groupId,
      defaultLanguage: data.defaultLanguage,
      operationMode: data.operationMode,
      emailAssignmentDuration: data.emailAssignmentDuration,
      codeExpirationMinutes: data.codeExpirationMinutes,
      lockerHoldMinutes: data.lockerHoldMinutes,
      supportedTagTypeIds: data.supportedTagTypeIds || []
    });
    
    return data;
  } catch (error) {
    console.error('Error updating general configuration:', error);
    throw error;
  }
};

export const HotelEdit: React.FC = () => {
  const notify = useNotify();
  const refresh = useRefresh();
  const dataProvider = useDataProvider() as any;

  const handleSave = async (values: any, { record }: any) => {
    try {
      if (values.operationMode === 'TAG_ONLY' && 
          (!values.supportedTagTypeIds || values.supportedTagTypeIds.length === 0)) {
        notify('Debe seleccionar al menos un tipo de tag para el modo Tag Only', { type: 'error' });
        throw new Error('Tag types required for TAG_ONLY mode');
      }

      await transformData(values, { dataProvider });
      
      notify('Configuración general actualizada con éxito', { type: 'success' });
      refresh();
      
      return values;
    } catch (error: any) {
      notify(error.message || 'Error al actualizar la configuración', { type: 'error' });
      throw error;
    }
  };

  return (
    <Edit 
      title={<HotelEditTitle />}
      mutationMode="pessimistic"
      mutationOptions={{
        onSuccess: () => {
          notify('Hotel actualizado con éxito', { type: 'success' });
        },
      }}
      transform={handleSave}
    >
      <MyFormContainer fullWidth={true}>
        <SimpleForm 
          toolbar={<MyToolbar showDelete={false} resource="hotels" />}
          sanitizeEmptyValues={false}
        >
          <HotelEditForm />
        </SimpleForm>
      </MyFormContainer>
    </Edit>
  );
};

export default HotelEdit;